import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, Search, Plus, ClipboardCheck, ArrowRight, Filter, Lightbulb } from "lucide-react";
import { C, StatusBadge, AudienceChips, fmtDate } from "./sopsUi";

export default function SopLibrary() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    api.get("/sops/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setItems(null);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (category !== "all") params.category = category;
    if (isAdmin && status !== "all") params.status = status;
    try {
      const { data } = await api.get("/sops", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(errMsg(e)); setItems([]); }
  }, [q, category, status, isAdmin]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="max-w-6xl mx-auto pb-16" data-testid="sop-library-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2" style={{ color: C.charcoal }}>
            <BookOpen size={22} style={{ color: C.navy }} /> SOP Library
          </h1>
          <p className="text-[13px] mt-0.5" style={{ color: C.slate }}>
            Step-by-step help for using the system. You see the guides available for your role.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button data-testid="sop-suggestions-link" variant="outline" onClick={() => navigate("/sops/suggestions")}
              className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Lightbulb size={15} /> Suggestions</Button>
            <Button data-testid="sop-review-link" variant="outline" onClick={() => navigate("/sops/review")}
              className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><ClipboardCheck size={15} /> Review queue</Button>
            <Button data-testid="sop-new-btn" onClick={() => navigate("/sops/new")}
              className="h-9 gap-1 bg-[#9C1E22] hover:bg-[#861A1D] text-white"><Plus size={15} /> New SOP</Button>
          </div>
        )}
      </div>

      <Card className="p-3 border-[#E6EDF2] mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: C.slate }} />
            <Input data-testid="sop-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search how-to guides…" className="h-9 pl-8 text-[13px]" />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 w-[190px] text-[13px]" data-testid="sop-filter-category">
              <Filter size={13} className="mr-1" style={{ color: C.navy }} /><SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              <SelectItem value="all">All categories</SelectItem>
              {cats.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}{c.count ? ` (${c.count})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 w-[150px] text-[13px]" data-testid="sop-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="pending_review">Pending review</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </Card>

      {items === null && <div className="grid sm:grid-cols-2 gap-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>}
      {items && items.length === 0 && (
        <Card className="p-10 text-center border-[#E6EDF2]" style={{ color: C.slate }} data-testid="sop-empty">
          No guides match. {isAdmin ? "Try a different filter or create a new SOP." : "Try a different search."}
        </Card>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {items?.map((s) => (
          <Card key={s.id} data-testid={`sop-card-${s.id}`} onClick={() => navigate(`/sops/${s.id}`)}
            className="p-4 border-[#E6EDF2] cursor-pointer hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-2">
              <span className="font-display font-semibold text-[15px]" style={{ color: C.navy }}>{s.title}</span>
              <StatusBadge status={s.status} testid={`sop-status-${s.id}`} />
            </div>
            <p className="text-[13px] mt-1 line-clamp-2" style={{ color: C.charcoal }}>{s.summary}</p>
            <div className="flex items-center justify-between gap-2 mt-3">
              <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(28,67,88,0.08)", color: C.navy }}>{s.category_label}</span>
              <span className="text-[11px]" style={{ color: C.slate }}>Updated {fmtDate(s.updated_at)}</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <AudienceChips audiences={s.audiences} />
              <ArrowRight size={14} style={{ color: C.red }} />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
